import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Phone, AlertTriangle, Heart, MessageCircle, Shield, ExternalLink, Bell, Loader2, CheckCircle2 } from 'lucide-react';

export default function CrisisButton() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const [open, setOpen] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [notified, setNotified] = useState(false);
  const [crisisMessage, setCrisisMessage] = useState('');

  const notifyTeam = async () => {
    setNotifying(true);
    try {
      const res = await api.post('/crisis/alert', {
        message: crisisMessage,
        location_text: '',
      });
      toast.success(`Your team has been alerted (${res.data.recipients_notified} contact${res.data.recipients_notified !== 1 ? 's' : ''}).`);
      setNotified(true);
    } catch (e) {
      toast.error('Could not reach your team — please call 988 or 911.');
    }
    setNotifying(false);
  };

  const resources = [
    {
      name: '988 Suicide & Crisis Lifeline',
      phone: '988',
      desc: 'Free, confidential support 24/7',
      icon: Phone,
      color: 'bg-red-50 text-red-700 border-red-200',
      iconColor: 'text-red-600',
    },
    {
      name: 'SAMHSA National Helpline',
      phone: '1-800-662-4357',
      desc: 'Treatment referral & information (24/7)',
      icon: Heart,
      color: 'bg-violet-50 text-violet-700 border-violet-200',
      iconColor: 'text-violet-600',
    },
    {
      name: 'Crisis Text Line',
      phone: 'Text HOME to 741741',
      desc: 'Free crisis support via text message',
      icon: MessageCircle,
      color: 'bg-sky-50 text-sky-700 border-sky-200',
      iconColor: 'text-sky-600',
    },
    {
      name: 'National Domestic Violence Hotline',
      phone: '1-800-799-7233',
      desc: 'Safety planning & support (24/7)',
      icon: Shield,
      color: 'bg-amber-50 text-amber-700 border-amber-200',
      iconColor: 'text-amber-600',
    },
  ];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-5 z-50 flex items-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 print:hidden"
        data-testid="crisis-support-button"
        aria-label="Get Crisis Support"
      >
        <AlertTriangle size={18} />
        <span className="text-sm font-semibold hidden sm:inline">Need Help Now</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} />
              Crisis Support Resources
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2" data-testid="crisis-resources-list">
            <p className="text-sm text-muted-foreground">
              If you or someone you know is in immediate danger, call <strong>911</strong>.
              These resources are free, confidential, and available 24/7.
            </p>

            {/* Notify My Team — auth'd users only */}
            {user && (
              <div className="p-4 rounded-xl border-2 border-rose-300 bg-rose-50" data-testid="notify-team-panel">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-rose-100">
                    <Bell size={20} className="text-rose-700" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-rose-800">Notify My Purpose Driven Team</p>
                    <p className="text-xs mt-0.5 text-rose-700">Your peer specialist + executive director will be paged immediately.</p>
                    {notified ? (
                      <div className="mt-2 flex items-center gap-1.5 text-sm text-emerald-700 font-semibold">
                        <CheckCircle2 size={14} /> Team alerted — someone is on the way.
                      </div>
                    ) : (
                      <>
                        <Textarea
                          value={crisisMessage}
                          onChange={(e) => setCrisisMessage(e.target.value)}
                          placeholder="(Optional) Briefly say what's going on..."
                          rows={2}
                          className="mt-2 text-xs bg-white"
                          data-testid="crisis-message-input"
                        />
                        <Button
                          onClick={notifyTeam}
                          disabled={notifying}
                          className="mt-2 w-full bg-rose-600 hover:bg-rose-700 text-white rounded-full gap-1.5"
                          data-testid="notify-team-btn"
                        >
                          {notifying ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
                          Page My Team Now
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            {resources.map((r, i) => (
              <div
                key={r.name || `resource-${i}`}
                className={`p-4 rounded-xl border ${r.color} transition-all hover:shadow-sm`}
                data-testid={`crisis-resource-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${r.color}`}>
                    <r.icon size={20} className={r.iconColor} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{r.name}</p>
                    <p className="text-xs mt-0.5 opacity-80">{r.desc}</p>
                    <a
                      href={r.phone.startsWith('1') || r.phone === '988' ? `tel:${r.phone.replace(/[^0-9]/g, '')}` : '#'}
                      className="inline-flex items-center gap-1.5 mt-2 text-sm font-bold hover:underline"
                    >
                      <Phone size={14} />
                      {r.phone}
                      {(r.phone.startsWith('1') || r.phone === '988') && <ExternalLink size={12} />}
                    </a>
                  </div>
                </div>
              </div>
            ))}
            <div className="pt-2 text-center">
              <p className="text-xs text-muted-foreground">
                You are not alone. Help is always available.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
